import { createAsyncThunk, createSlice, isFulfilled, isRejected } from "@reduxjs/toolkit";
import { duplicateFilter, updateCorpSavedFilters } from "components/common/Filters/Filters.slice";
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from "utils/APIHelper";
import { SME_PAGE_SIZE } from "utils/CommonConstants";
import { responseTypes, serviceTypes } from "utils/Types";

const sortingInitial = {
  plansSort: {
    recentSortingApplied: {
      property: "planTitle",
      order: "asc"
    }
  },
  propertiesSort: {
    recentSortingApplied: {
      property: "inncode",
      order: "asc"
    }
  }
};
const initialState: any = {
  dialogs: {
    renameList: { isOpen: false, data: {} },
    deleteList: { isOpen: false, data: {}, listType: "" },
    deleteSCList: { isOpen: false, data: {} },
    saveList: { isOpen: false, data: {} }
  },
  myView: {
    properties: { overview: [], list: [], totalRecords: 0 },
    plans: { overview: [], list: [], totalRecords: 0 }
  },
  corpLoader: false,
  summaryLoader: false,
  overviewLoader: false,
  myListsSelected: {
    listCategory: "bookmarked",
    selectedList: {},
    navigatedList: ""
  },
  createListError: {
    isError: false,
    message: "",
    isInputError: false
  },
  selectedTabs: {
    corpView: 0,
    dashboard: 0,
    myList: 0
  },
  recentlyViewedData: {},
  recentlyViewedSelected: {
    category: "",
    selectedItem: ""
  },
  isCreateInnCodeModal: false,
  listCreatedSuccess: {
    isCreated: false,
    message: ""
  },
  listEditSuccess: {
    userList: [],
    listData: null,
    type: ""
  },
  isEditListModal: false,
  selectedMappingIds: [],
  selectedUserList: [],

  sorting: {
    dashboard: sortingInitial,
    myList: sortingInitial
  }
};
function isRejectedAction(action: any) {
  return action.type.endsWith("rejected");
}

export const fetchCorpDashboardPropertiesAsync = createAsyncThunk(
  "Corp_Dashboard/view/properties",
  async (data: any, thunkApi) => {
    const API_URL = `view/properties/summary`;
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }
    return response.data;
  }
);

export const fetchCorpDashboardPlansAsync = createAsyncThunk(
  "Corp_Dashboard/view/plans/summary",
  async (data: any, thunkApi) => {
    const API_URL = `view/plans/summary`;
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }

    return response.data;
  }
);

export const fetchCorpDashboardPropertiesOverviewAsync = createAsyncThunk(
  "Corp_Dashboard/view/properties/overview",
  async (data: any, thunkApi) => {
    const API_URL = `view/properties/overview`;
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }

    return response.data;
  }
);

export const fetchCorpDashboardPlansOverviewAsync = createAsyncThunk(
  "Corp_Dashboard/view/plans/summary/overview",
  async (data: any, thunkApi) => {
    const API_URL = `view/plans/overview`;
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }

    return response.data;
  }
);

export const fetchRecentlyViewedData = createAsyncThunk(
  "Corp_Dashboard/fetchRecentlyViewed",
  async (data: any, thunkApi) => {
    const API_URL = "recent-views";
    const response: any = await axiosGet(API_URL, serviceTypes.userplan);
    if (
      response?.response?.status >= responseTypes.internal_error &&
      response?.response?.status < responseTypes.other_error
    ) {
      return thunkApi.rejectWithValue(response.response);
    }
    return response.data;
  }
);

export const updateRecentlyViewedData = createAsyncThunk(
  "Corp_Dashboard/updateRecentlyViewed",
  async (data: any, thunkApi) => {
    const API_URL = "recent-views";
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (
      response?.response?.status >= responseTypes.internal_error &&
      response?.response?.status < responseTypes.other_error
    ) {
      return thunkApi.rejectWithValue(response.response);
    }
  }
);

export const deleteMyList = createAsyncThunk(`Corp_Dashboard/deleteMyList`, async (data: any, thunkApi) => {
  const { fulfillWithValue, rejectWithValue } = thunkApi;
  const API_URL = `corp/filters?ids=${data.id}`;
  const response = await axiosDelete(API_URL, serviceTypes.userplan);

  if (response.status === responseTypes.success) {
    return fulfillWithValue(response.data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const deleteSCList = createAsyncThunk(`Corp_Dashboard/deleteSCList`, async (data: any, thunkApi) => {
  const { fulfillWithValue, rejectWithValue } = thunkApi;
  const API_URL = `inncode-groups?innCodeGroupIds=${data}`;
  const response = await axiosDelete(API_URL, serviceTypes.userplan);

  if (response.status === responseTypes.success) {
    return fulfillWithValue(response.data);
  } else {
    return rejectWithValue(response.response.data);
  }
});

export const fetchSCListById = createAsyncThunk("fetchSCListById", async (data: any, thunkApi: any) => {
  const API_URL = `inncode-groups/${data?.id}?view=${data?.view}&page=${data?.page || 0}&engagement=${
    data?.engagement || ""
  }&inncode=${data.innCode || ""}&size=${data?.size || SME_PAGE_SIZE}`;
  const response: any = await axiosGet(API_URL, serviceTypes.userplan);
  return response;
});

export const addMemberToList = createAsyncThunk("addmemberstoList", async (data: any, thunkApi: any) => {
  const API_URL = `inncode-groups/${data?.id}?updateType=addOrUpdateInncodeGroupMappings`;
  const response: any = await axiosPatch(API_URL, serviceTypes.userplan, { newMappings: data.mappings });
  return response;
});

export const fetchIndividualPropertyAsync = createAsyncThunk(
  "Corp_Dashboard/fetchIndividual/properties",
  async (data: any, thunkApi) => {
    const API_URL = `view/inncode-group/summary`;
    const response: any = await axiosPost(API_URL, serviceTypes.userplan, data);
    if (response?.response?.status >= responseTypes.error) {
      return thunkApi.rejectWithValue(response.response);
    }
    return response.data;
  }
);

export const DashboardSlice = createSlice({
  name: "Corp_Dashboard",
  initialState,
  reducers: {
    reset: (state) => {
      state.myView = {
        properties: { overview: [], list: [], totalRecords: 0 },
        plans: { overview: [], list: [], totalRecords: 0 }
      };
    },
    setCorpLoader: (state, action) => {
      state.corpLoader = action.payload;
    },
    setSelectedList: (state, action) => {
      state.myListsSelected.selectedList = action.payload;
    },
    setSelectedListCategory: (state, action) => {
      state.myListsSelected.listCategory = action.payload;
    },
    setCorpViewSelectedTab: (state, action) => {
      state.selectedTabs[action.payload.type] = action.payload.val;
    },
    resetMyListsData: (state, action) => {
      state.myListsSelected.selectedList = action.payload;
      state.myListsSelected.listCategory = "bookmarked";
      state.selectedTabs["myList"] = 0;
    },
    setNavigatedMyList: (state, action) => {
      state.myListsSelected.navigatedList = action.payload;
    },
    setRecentlyViewed: (state, action) => {
      state.recentlyViewedSelected.category = action.payload.category;
      state.recentlyViewedSelected.selectedItem = action.payload.item;
    },
    setIsCreateInnCodeModal: (state, action) => {
      state.isCreateInnCodeModal = action?.payload;
    },
    setUploadOrCreate: (state, action) => {
      state.uploadOrCreate = action?.payload;
    },
    setCreateListError: (state, action) => {
      state.createListError = action.payload;
    },
    setCreatedSuccess: (state, action) => {
      state.listCreatedSuccess = action.payload;
    },
    setEditListSuccess: (state, action) => {
      state.listEditSuccess = action.payload;
    },
    setIsEditListModal: (state, action) => {
      state.isEditListModal = action?.payload;
    },
    setDialog: (state, action) => {
      const { dialogName, isOpen, data, listType } = action.payload;
      state.dialogs[dialogName] = { isOpen, data, listType };
    },
    setPropertiesListRedux: (state, action) => {
      state.myView.properties.list = action.payload;
    },
    setSelectedMappingIds: (state, action) => {
      state.selectedMappingIds = action.payload;
    },
    setSelectedUserList: (state, action) => {
      state.selectedUserList = action.payload;
    },
    setSorting: (state, action) => {
      const { type, tab, header, order, isSortedAsc } = action.payload;
      state.sorting[type][tab]["recentSortingApplied"] = { property: header, order };
    },
    resetSorting: (state, action) => {
      const { type, header, order, isSortedAsc } = action.payload;
      state.sorting[type] = sortingInitial;
      state.sorting[type] = sortingInitial;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCorpDashboardPropertiesAsync.pending, (state, action: any) => {
        state.summaryLoader = action.meta.arg.pageable.page === 0;
      })
      .addCase(fetchCorpDashboardPropertiesAsync.fulfilled, (state, action: any) => {
        state.myView.properties.list =
          action.meta.arg.pageable.page === 0
            ? action?.payload?.properties
            : [...state.myView.properties.list, ...action.payload.properties];
        state.myView.properties.totalRecords = action.payload?.meta?.page?.total;
        state.summaryLoader = false;
      })
      .addCase(fetchCorpDashboardPropertiesAsync.rejected, (state, action: any) => {
        state.summaryLoader = false;
      })
      .addCase(fetchCorpDashboardPlansAsync.pending, (state, action: any) => {
        state.summaryLoader = true;
      })
      .addCase(fetchCorpDashboardPlansAsync.fulfilled, (state, action: any) => {
        state.myView.plans.list = action.payload.plans;
        state.summaryLoader = false;
      })
      .addCase(fetchCorpDashboardPlansAsync.rejected, (state, action: any) => {
        state.summaryLoader = false;
      })
      .addCase(fetchCorpDashboardPropertiesOverviewAsync.pending, (state, action: any) => {
        state.overviewLoader = true;
      })
      .addCase(fetchCorpDashboardPropertiesOverviewAsync.fulfilled, (state, action: any) => {
        state.myView.properties.overview = action.payload;
        state.overviewLoader = false;
      })
      .addCase(fetchCorpDashboardPropertiesOverviewAsync.rejected, (state, action: any) => {
        state.overviewLoader = false;
      })
      .addCase(fetchCorpDashboardPlansOverviewAsync.pending, (state, action: any) => {
        state.overviewLoader = true;
      })
      .addCase(fetchCorpDashboardPlansOverviewAsync.fulfilled, (state, action: any) => {
        state.myView.plans.overview = action.payload;
        state.overviewLoader = false;
      })
      .addCase(fetchCorpDashboardPlansOverviewAsync.rejected, (state, action: any) => {
        state.overviewLoader = false;
      })
      .addCase(fetchRecentlyViewedData.pending, (state) => {
        state.corpLoader = true;
      })
      .addCase(fetchRecentlyViewedData.fulfilled, (state, action: any) => {
        state.corpLoader = false;
        state.recentlyViewedData = action.payload;
      })
      .addCase(fetchRecentlyViewedData.rejected, (state, action: any) => {
        state.corpLoader = false;
      })
      .addCase(fetchSCListById.pending, (state) => {
        state.corpLoader = true;
      })
      .addCase(fetchSCListById.fulfilled, (state, action: any) => {
        state.corpLoader = false;
      })
      .addCase(fetchSCListById.rejected, (state) => {
        state.corpLoader = false;
      })
      .addCase(fetchIndividualPropertyAsync.pending, (state) => {
        state.corpLoader = true;
      })
      .addCase(fetchIndividualPropertyAsync.fulfilled, (state, action: any) => {
        state.corpLoader = false;
      })
      .addCase(fetchIndividualPropertyAsync.rejected, (state) => {
        state.corpLoader = false;
      })
      .addCase(addMemberToList.pending, (state) => {
        state.corpLoader = true;
      })
      .addCase(addMemberToList.fulfilled, (state, action: any) => {
        state.corpLoader = false;
      })
      .addCase(addMemberToList.rejected, (state) => {
        state.corpLoader = false;
      })
      .addCase(duplicateFilter.fulfilled, (state, action: any) => {
        state.myListsSelected.selectedList = action.payload[action.meta.arg.toFilterType].find(
          (item: any) => item.name === action.meta.arg.name
        );
        state.myListsSelected.listCategory = action.meta.arg.toFilterType;
      })
      .addCase(updateCorpSavedFilters.fulfilled, (state, action: any) => {
        if (action.meta.arg.actionType === "rename") {
          state.myListsSelected.selectedList = action.payload?.data["dynamic"].find(
            (item: any) => item?.id === action.meta.arg.dynamic[0]?.id
          );
          state.myListsSelected.listCategory = "dynamic";
        }
      })
      .addMatcher(isFulfilled(deleteMyList, deleteSCList), (state) => {
        state.dialogs.deleteList = { isOpen: false, data: {} };
        state.dialogs.deleteSCList = { isOpen: false, data: {} };
        state.myListsSelected.selectedList = {};
        state.myListsSelected.listCategory = "bookmarked";
      })
      .addMatcher(isRejected(deleteMyList, deleteSCList), (state) => {
        state.dialogs.deleteList = { isOpen: false, data: {} };
        state.dialogs.deleteSCList = { isOpen: false, data: {} };
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.corpLoader = false;
      });
  }
});

export const {
  setCorpLoader,
  setSelectedListCategory,
  setSelectedList,
  setCorpViewSelectedTab,
  resetMyListsData,
  setNavigatedMyList,
  setRecentlyViewed,
  setIsCreateInnCodeModal,
  setCreatedSuccess,
  setCreateListError,
  setEditListSuccess,
  setIsEditListModal,
  setDialog,
  setPropertiesListRedux,
  setSelectedUserList,
  setSorting,
  setUploadOrCreate,
  resetSorting,
  reset
} = DashboardSlice.actions;
export default DashboardSlice.reducer;
